import { FC, memo, useEffect, useMemo } from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { LabelIcon } from '@/components/EventHeading/styles'
import { Loader } from '@/components/Loader'
import { TileDescription } from '@/components/Tiles/TileDescription'
import { TileImage } from '@/components/Tiles/TileImage'
import { LineTileShape, LineTileSize } from '@/constants/common'
import { icons, imageType, sliderItemImgPlaceholder } from '@/constants/icons'
import { eventSliderLogoWidth } from '@/constants/tiles'
import { useActiveBroadcast } from '@/hooks'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { useTileStatusStore } from '@/store/useTileStatusStore'
import { TileProps } from '@/types/componentsPropsTypes'
import { eventTypes, tileTypes } from '@/types/servicesTypes/configTypes'
import { checkIsEventPlayable, getBadges } from '@/utils/eventUtils'
import { findBestImage, findNearestByImgWidth } from '@/utils/utils'

import {
  TileElem,
  TilePlay,
  TilePreviewWrapper,
  TileOversizeInfo,
  TileLineElem,
  TileDebugInfo,
  BadgesWrapper,
  TileTimeLabel,
  TilePremiumLabel,
  TilePurchasedLabel,
  TileNonWatchableLabel,
} from './styles'
import { getTileProps } from '../Lines/utils'

const TileRaw: FC<TileProps> = ({
  focusId,
  data: tileData,
  handleToggle,
  isChannelSlider = false,
  tileShape = LineTileShape.ASPECT16x9,
  tileSize = LineTileSize.DEFAULT,
  broadcastId,
  isGridTile,
  onMouseOverTile,
  isHiddenImage = false,
  isLineTile,
  isActive,
}) => {
  const { t } = useCommonTranslation()
  const setFocus = useSetFocus()
  const { isRegisteredUser } = useAuth()

  const appSettings = useStore((state) => state.common.appSettings)
  const { isDisabledImages, isDevToolsUsed } = appSettings
  const { activeChannel } = useActiveBroadcast(broadcastId)

  const { image, previewImage, data, preview, type, id, showTitle } = tileData
  const channelEvent = useStore((state) => state.common.channelEvents[id])
  const currentChannelEvent = channelEvent?.isFutureChannelEvent
    ? null
    : channelEvent

  const isTileLoading = useTileStatusStore(
    (state) => state.tileIsLoadingById[focusId],
  )
  const isChannelTile = type === tileTypes.CHANNEL

  const event =
    isChannelSlider || isChannelTile ? currentChannelEvent : data?.event

  const currChannel = isChannelSlider
    ? activeChannel
    : isChannelTile
    ? data?.channel
    : null
  const channelSources =
    currChannel?.image?.sources || event?.channelLogo?.sources

  const { isAccessible, isPremium, isPPVEvent } = useMemo(() => {
    return getContentAccessFlags(event, currChannel, isRegisteredUser)
  }, [event, currChannel, isRegisteredUser])

  const isPageTile = type === tileTypes.PAGE
  const isEventTile = type === tileTypes.EVENT
  const isEventChannel = event?.type === eventTypes.CHANNEL
  const isEventVod = event?.type === eventTypes.VOD
  const isEventDeepLink = event?.type === eventTypes.DEEP_LINK
  const isEventYoutube = event?.type === eventTypes.YOUTUBE
  const hasEventType =
    isEventChannel || isEventVod || isEventDeepLink || isEventYoutube
  const isActiveChannel =
    tileData.id === activeChannel?.id ? 'isActiveChannel' : ''

  const isWatchable = Boolean(
    isChannelSlider || isChannelTile
      ? currChannel?.watchable
      : event?.watchable,
  )

  const focusNode = useFocusNodeById(focusId)

  useEffect(() => {
    if (isChannelSlider && id === activeChannel?.id) {
      setTimeout(() => setFocus(focusId), 0)
    }
  }, [])

  const {
    isLiveNow,
    isTodayEvent,
    timeLabel,
    isPastEvent,
    isBlockedPremiumChannel,
    isBlockedPPVEvent,
    isBlockedPromotionEvent,
    isSubscribedPremiumChannel,
    isPurchasedPPVEvent,
  } = useMemo(
    () => getBadges(isAccessible, isPPVEvent, isPremium, isWatchable, event),
    [event, isAccessible, isPPVEvent, isPremium, isWatchable],
  )

  const isPageTileWithTitle = Boolean(isPageTile && showTitle)
  const showName = isEventTile ? showTitle ?? true : !!showTitle

  const isPlayableEvent = useMemo(() => {
    return checkIsEventPlayable(data?.event)
  }, [data?.event])

  const showPlayIcon = isPlayableEvent && isAccessible

  const showTimeIcon =
    isChannelTile || isChannelSlider || (!!timeLabel && !isPastEvent)
  const showNonWatchableLabel =
    (isEventTile || isChannelTile || isChannelSlider) && !isWatchable

  const showTileImage = !(isHiddenImage || isDisabledImages)

  const channelLogoImg = useMemo(
    () =>
      findNearestByImgWidth(channelSources, eventSliderLogoWidth, {
        prioritizeSvg: true,
      }),
    [currChannel?.id],
  )

  const primarySources = previewImage?.sources || image?.sources
  const imgSources = isChannelSlider
    ? event?.image?.sources || primarySources
    : primarySources

  const img = useMemo(
    () =>
      findBestImage(imgSources, {
        targetWidth: getTileProps(tileShape, tileSize).w,
        targetShape: tileShape,
      }),
    [tileData?.id],
  )

  const previewUrl = useMemo(
    () =>
      preview?.image?.sources?.find(
        (img) => img?.format.toLowerCase() === imageType.WEBP,
      )?.url,
    [tileData?.id],
  )

  const showPreview = Boolean(
    previewUrl &&
      (preview?.autoPlay || focusNode?.isFocused || isActive) &&
      !isHiddenImage &&
      !isChannelSlider,
  )

  const showOversizeInfo =
    isDevToolsUsed && isDevMode && !!(img?.oversize || channelLogoImg?.oversize)

  const onSelected = () => {
    if (!isGridTile) {
      handleToggle?.({ tileData, focusId })
    }
  }

  const renderTileImage = () => {
    return (
      <TileImage
        url={img?.url}
        className={isChannelSlider && !event ? 'channel_img' : ''}
        errorPlaceholder={sliderItemImgPlaceholder}
      />
    )
  }

  const renderTilePreview = () => {
    return (
      <TilePreviewWrapper>
        <TileImage url={previewUrl} />
      </TilePreviewWrapper>
    )
  }

  const tileClassName =
    (isGridTile ? 'grid__item ' : 'slider__item ') +
    (isChannelSlider ? `is_channel ${isActiveChannel} ` : '')

  const TileComponent: React.ElementType = isLineTile ? TileLineElem : TileElem
  const tileProps = isLineTile
    ? { onClick: onSelected }
    : { onSelected: onSelected, focusId }

  const showTileInfo =
    tileShape === LineTileShape.ASPECT16x9 && tileSize !== LineTileSize.XSMALL

  const renderTileInfo = () => {
    return (
      <>
        <TileDescription
          event={isChannelTile ? null : event}
          focusId={focusId}
          tileData={tileData}
          isChannelSlider={isChannelSlider}
          tileSize={tileSize}
          channelLogoImg={channelLogoImg?.url}
          hasEventType={hasEventType}
          showName={showName || isChannelSlider}
          isLiveNow={isLiveNow}
          isActive={isActive}
          isPageTileWithTitle={isPageTileWithTitle}
        />
        {showPlayIcon && (
          <TilePlay className="play__item" src={icons.slider.playIcon} alt="" />
        )}
        {showTimeIcon && (
          <TileTimeLabel
            $isLive={isChannelTile || isChannelSlider}
            $isLiveNow={isLiveNow && !isChannelTile}
            $isToday={isTodayEvent}
          >
            {isChannelTile || (isChannelSlider && !timeLabel)
              ? t('liveTV')
              : timeLabel}
          </TileTimeLabel>
        )}
        <BadgesWrapper>
          {isBlockedPPVEvent && (
            <TilePremiumLabel>
              <LabelIcon src={icons.navigation.blockedIcon} />
              <span>
                {isBlockedPromotionEvent
                  ? t('ppvPopup.promotion')
                  : t('ppvPopup.payPerView')}
              </span>
            </TilePremiumLabel>
          )}
          {isBlockedPremiumChannel && (
            <TilePremiumLabel>
              <LabelIcon src={icons.navigation.blockedIcon} />
              <span>{t('premiumPass')}</span>
            </TilePremiumLabel>
          )}
          {(isSubscribedPremiumChannel || isPurchasedPPVEvent) && (
            <TilePurchasedLabel>
              <LabelIcon src={icons.navigation.subscribedIcon} />
              <span>
                {isSubscribedPremiumChannel ? t('subscribed') : t('bought')}
              </span>
            </TilePurchasedLabel>
          )}
          {showNonWatchableLabel && (
            <TileNonWatchableLabel>
              <LabelIcon src={icons.navigation.infoIcon} />
              <span>{t('programInfoOnly')}</span>
            </TileNonWatchableLabel>
          )}
        </BadgesWrapper>
      </>
    )
  }
  return (
    <TileComponent
      data-tile-type={type}
      className={tileClassName}
      $tileShape={tileShape}
      $tileSize={tileSize}
      onMouseEnter={() => onMouseOverTile?.(tileData, focusId)}
      {...tileProps}
    >
      {showTileImage && renderTileImage()}
      {showPreview && renderTilePreview()}
      {showTileInfo && renderTileInfo()}
      {showOversizeInfo && (
        <TileOversizeInfo>
          ⚠️ Oversize:
          {img?.oversize && <span>Img x {img?.oversize}</span>}
          {channelLogoImg?.oversize && (
            <span>Logo x{channelLogoImg?.oversize}</span>
          )}
        </TileOversizeInfo>
      )}
      {isTileLoading && <Loader />}
      {isDevMode && (
        <TileDebugInfo>
          {tileData.type}
          {tileData.data?.pageId && ` | ${tileData.data.pageId}`}
          {` | showTitle: ${showTitle}`}
        </TileDebugInfo>
      )}
    </TileComponent>
  )
}

export const Tile = memo(TileRaw)
